import type { ZodValidator } from '@tanstack/zod-form-adapter';

import { useElectricalProject } from '@connected/elektra-sdk/hooks/use-project';
import { elektraAppQueryOptions } from '@connected/elektra-sdk/queries/query-options';
import { Label } from '@connected/form-fields/components/label';
import { SingleCheckOptionItem, SingleOptionField } from '@connected/form-fields/components/option';
import { Accordion } from '@connected/ui/components/accordion';
import { Skeleton } from '@connected/ui/components/skeleton';
import { booleanDefaultValue } from '@connected/ui/lib/utils';
import { useForm } from '@tanstack/react-form';
import { useQuery } from '@tanstack/react-query';
import { zodValidator } from '@tanstack/zod-form-adapter';
import { useMemo } from 'react';

import { ElectricalInspectionKind } from '@/components/electrical-inspection-kind';

import type { CompletionFormValues } from '../utils/completion-types';

import { useSelectedProjectUserMutation } from '../hooks/use-completion-mutations';
import { defaultCompletionFormValues, INTERVAL_QUESTION_INSPECTION_KINDS } from '../utils/completion-types';
import { CompletionReportFields } from './completion-report-fields';
import { IntervalQuestionsFields } from './interval-questions-fields';

type CompletionFormProps = {
    projectVersionId: string;
};

export function CompletionForm({ projectVersionId }: CompletionFormProps) {
    const { context, inspectionKindsArray } = useElectricalProject();

    const conclusionQuery = useQuery(elektraAppQueryOptions.project.inspection.conclusion.conclusionEditOptions({ electricalProjectVersionId: projectVersionId }));
    const scheduleQuery = useQuery(elektraAppQueryOptions.project.schedule.projectUsersListOptions({ electricalProjectVersionId: projectVersionId, userKind: 'CertifiedInspector' }));

    const defaultValues = useMemo<CompletionFormValues>(() => {
        const currentConclusion = conclusionQuery.data?.Inspection;
        const currentIntervalQuestions = currentConclusion?.IntervalQuestions;
        const currentScopeInspection = currentConclusion?.ScopeInspection;
        const currentReports = currentConclusion?.Reports;

        return {
            intervalDeterminedByKind: currentConclusion?.IntervalDeterminedByKind || defaultCompletionFormValues.intervalDeterminedByKind,
            intervalQuestions: {
                determineInspectionIntervalAge: currentIntervalQuestions?.DetermineInspectionIntervalAge || defaultCompletionFormValues.intervalQuestions.determineInspectionIntervalAge,
                determineInspectionIntervalEnvironment: currentIntervalQuestions?.DetermineInspectionIntervalEnvironment || defaultCompletionFormValues.intervalQuestions.determineInspectionIntervalEnvironment,
                determineInspectionIntervalQuality: currentIntervalQuestions?.DetermineInspectionIntervalQuality || defaultCompletionFormValues.intervalQuestions.determineInspectionIntervalQuality,
                determineInspectionIntervalSupervision: currentIntervalQuestions?.DetermineInspectionIntervalSupervision || defaultCompletionFormValues.intervalQuestions.determineInspectionIntervalSupervision,
                determineInspectionIntervalUsers: currentIntervalQuestions?.DetermineInspectionIntervalUsers || defaultCompletionFormValues.intervalQuestions.determineInspectionIntervalUsers,
            },
            scopeInspection: { sciosInspectionCode: currentScopeInspection?.SciosInspectionCode || defaultCompletionFormValues.scopeInspection.sciosInspectionCode },
            reports: inspectionKindsArray.map((inspectionKind): CompletionFormValues['reports'][number] => {
                const report = currentReports?.find(report => report.InspectionKind === inspectionKind);

                return {
                    inspectionKind,
                    intervalDeterminedByKind: report?.IntervalDeterminedByKind || defaultCompletionFormValues.intervalDeterminedByKind,
                    nextInspectionInYearCount: report?.NextInspectionInYearCount || defaultCompletionFormValues.nextInspectionInYearCount,
                    sciosInspectionCode: report?.SciosInspectionCode || defaultCompletionFormValues.scopeInspection.sciosInspectionCode,
                    isFinalVerdictPassed: booleanDefaultValue(report?.IsFinalVerdictPassed),
                    isPerformedAccordingToInspectionPlan: booleanDefaultValue(report?.IsPerformedAccordingToInspectionPlan),
                    includeRepairStatement: booleanDefaultValue(report?.IncludeRepairStatement),
                    performedAccordingToInspectionPlanRemark: report?.PerformedAccordingToInspectionPlanRemark || defaultCompletionFormValues.performedAccordingToInspectionPlanRemark,
                };
            }),
            isFinalVerdictPassed: booleanDefaultValue(currentConclusion?.IsFinalVerdictPassed),
            isPerformedAccordingToInspectionPlan: booleanDefaultValue(currentConclusion?.IsPerformedAccordingToInspectionPlan),
            includeRepairStatement: booleanDefaultValue(currentConclusion?.IncludeRepairStatement),
            performedAccordingToInspectionPlanRemark: currentConclusion?.PerformedAccordingToInspectionPlanRemark || defaultCompletionFormValues.performedAccordingToInspectionPlanRemark,
            nextInspectionInYearCount: currentConclusion?.NextInspectionInYearCount || defaultCompletionFormValues.nextInspectionInYearCount,
        };
    }, [conclusionQuery.data?.Inspection, inspectionKindsArray]);

    const completionForm = useForm<CompletionFormValues, ZodValidator>({
        defaultValues,
        validatorAdapter: zodValidator(),
    });

    const selectedProjectUserMutation = useSelectedProjectUserMutation({ projectVersionId });

    async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        await completionForm.handleSubmit();
    }

    async function handleUserChange(userId: string) {
        await selectedProjectUserMutation.mutateAsync({
            body: {
                ElectricalProjectVersionId: projectVersionId,
                UserId: userId,
                Selected: true,
                Kind: 'CertifiedInspector',
            },
        });
    }

    const selectedIntervalQuestionKindsCount = inspectionKindsArray.filter(kind => INTERVAL_QUESTION_INSPECTION_KINDS.includes(kind)).length;
    const showIntervalQuestionsAtFormLevel = selectedIntervalQuestionKindsCount > 1;

    if (conclusionQuery.isLoading) {
        return (
            <>
                <Skeleton className="h-40" />
            </>
        );
    }

    return (
        <form onSubmit={onSubmit} className="flex flex-col gap-4">
            {/* Eindverantwoordelijke selector - Project level */}
            <Label
                label="Gecontroleerd door"
                className="w-full"
            >
                <SingleOptionField
                    name="certifiedInspector"
                    handleChange={handleUserChange}
                    state={{ value: scheduleQuery.data?.Users.find(user => user.Selected)?.UserId ?? '' }}
                    className="flex flex-row items-center justify-between"
                    options={scheduleQuery.data?.Users.map(user => ({
                        label: user.DisplayName!,
                        value: user.UserId!,
                    })) ?? []}
                    render={SingleCheckOptionItem}
                    dropdown
                    closeOnClick
                    emptyOptionsMessage="Geen beschikbare inspecteurs gevonden"
                    placeholder="Selecteer beschikbare inspecteur"
                />
            </Label>

            {/* If no inspection kinds are selected, show a message */}
            {context.selectedInspectionKinds.size === 0 && (
                <div className="flex flex-col gap-4">
                    <h2 className="text-lg font-semibold">Afronding per inspectie</h2>
                    <p className="text-sm text-gray-500">Geen inspecties geselecteerd</p>
                </div>
            )}

            {/* Single classification UI (single inspectionKind selected) */}
            {context.selectedInspectionKinds.size === 1 && (
                <CompletionReportFields
                    form={completionForm}
                    reportIndex={0}
                    inspectionKind={inspectionKindsArray[0]}
                    projectVersionId={projectVersionId}
                    conclusionData={conclusionQuery.data}
                />
            )}

            {/* Interval questions at form level when more than one interval question inspection kind is selected */}
            {showIntervalQuestionsAtFormLevel && (
                <IntervalQuestionsFields
                    form={completionForm}
                    projectVersionId={projectVersionId}
                    conclusionData={conclusionQuery.data}
                />
            )}

            {/* Multi-inspection reports UI (multiple inspectionKinds selected) */}
            {context.selectedInspectionKinds.size > 1 && (
                <div className="flex flex-col gap-4">
                    <h2 className="text-lg font-semibold">Afronding per inspectie</h2>
                    <Accordion type="single" defaultValue={inspectionKindsArray[0]} collapsible>
                        {inspectionKindsArray.map((inspectionKind, index) => (
                            <Accordion.Item key={inspectionKind} value={inspectionKind}>
                                <Accordion.Trigger>
                                    <h3 className="text-base font-semibold">
                                        <ElectricalInspectionKind inspectionKind={inspectionKind} />
                                    </h3>
                                </Accordion.Trigger>
                                <Accordion.Content className="flex flex-col gap-6">
                                    <CompletionReportFields
                                        form={completionForm}
                                        reportIndex={index}
                                        inspectionKind={inspectionKind}
                                        projectVersionId={projectVersionId}
                                        conclusionData={conclusionQuery.data}
                                        showIntervalQuestions={!showIntervalQuestionsAtFormLevel}
                                    />
                                </Accordion.Content>
                            </Accordion.Item>
                        ))}
                    </Accordion>
                </div>
            )}
        </form>
    );
}
